"use client";

import { useState } from "react";
import {
  FieldControl,
  FieldError,
  FieldLabel,
  FieldRoot,
} from "@tillix/ui-atoms/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
} from "@tillix/ui-atoms/input-group";
import { Controller, useFormContext } from "react-hook-form";
import {
  formatCardNumber,
  PaymentIcon,
  sanitizeCardNumber,
  validateCardNumber,
} from "react-svg-credit-card-payment-icons";
import { useT } from "@/features/i18n/hooks/use-t";
import { CardNetworkContext } from "../hooks/cardNetworkContext";
import { useCardNetwork } from "../hooks/useCardNetwork";

function maskAllButLastFour(digits: string, maskChar = "•"): string {
  if (digits.length <= 4) return formatCardNumber(digits);
  const formatted = formatCardNumber(digits);
  const totalDigits = digits.length;
  let digitsSeen = 0;
  let out = "";
  for (const ch of formatted) {
    if (/\d/.test(ch)) {
      digitsSeen += 1;
      out += digitsSeen <= totalDigits - 4 ? maskChar : ch;
    } else {
      out += ch;
    }
  }
  return out;
}

export interface CardNumberInputProps {
  /** RHF field name (defaults to "cardNumber"). */
  name?: string;
  /** HTML id — unique per form. */
  id: string;
  /** Content rendered on the same row as the card number field (e.g. CVV). */
  inlineEnd?: React.ReactNode;
  /** Remaining children rendered below the card number row. */
  children: React.ReactNode;
}

export function CardNumberInput({
  name = "cardNumber",
  id,
  inlineEnd,
  children,
}: CardNumberInputProps) {
  const { control } = useFormContext();
  const { t } = useT();
  const { t: tInputs } = useT("inputs");
  const network = useCardNetwork(name);
  const [isFocused, setIsFocused] = useState(false);

  const cardField = (
    <Controller
      control={control}
      name={name}
      render={({ field, fieldState }) => {
        const digits = field.value as string;
        const isValid = digits.length > 0 && validateCardNumber(digits);
        const display = isFocused
          ? formatCardNumber(digits)
          : isValid
            ? maskAllButLastFour(digits, "•")
            : formatCardNumber(digits);

        return (
          <FieldRoot name={field.name}>
            <FieldLabel htmlFor={id}>{t("paymentForm.cardNumber")}</FieldLabel>
            <FieldControl
              render={(defaultProps) => (
                <InputGroup>
                  <InputGroupAddon align="inline-start">
                    <PaymentIcon
                      className="opacity-100"
                      format="flatRounded"
                      type={network.type}
                      width={28}
                    />
                  </InputGroupAddon>
                  <InputGroupInput
                    {...defaultProps}
                    autoComplete="cc-number"
                    id={id}
                    inputMode="numeric"
                    maxLength={
                      network.maxCardLength +
                      Math.floor(network.maxCardLength / 4)
                    }
                    name={field.name}
                    onBlur={() => {
                      setIsFocused(false);
                      field.onBlur();
                    }}
                    onChange={(e) => {
                      const next = sanitizeCardNumber(e.target.value).slice(
                        0,
                        network.maxCardLength,
                      );
                      field.onChange(next);
                    }}
                    onFocus={() => setIsFocused(true)}
                    placeholder="1234 5678 9012 3456"
                    ref={field.ref}
                    value={display}
                  />
                </InputGroup>
              )}
            />
            <FieldError>
              {fieldState.error
                ? tInputs("validation.invalidCardNumber")
                : undefined}
            </FieldError>
          </FieldRoot>
        );
      }}
    />
  );

  return (
    <CardNetworkContext.Provider value={network}>
      {inlineEnd ? (
        <div className="grid grid-cols-[1fr_7rem] gap-3">
          {cardField}
          {inlineEnd}
        </div>
      ) : (
        cardField
      )}
      {children}
    </CardNetworkContext.Provider>
  );
}
